import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend, LineChart, Line } from "recharts";
import { Wallet, Upload, FileText, Pencil, Trash2, TrendingUp, Sparkles, AlertTriangle, Users, Loader2, Receipt, Download, RefreshCw, Folder, FolderOpen, ArrowUpRight, ArrowDownRight, Minus, Percent } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, apiErrorMessage } from "@/lib/api";

const eur = (n) => (n == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n));
const MESES_NOM = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

async function openPdf(id) {
  try {
    const res = await api.get(`/rrhh/nominas/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(apiErrorMessage(e)); }
}

async function downloadPdf(id, filename) {
  try {
    const res = await api.get(`/rrhh/nominas/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "nomina.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(apiErrorMessage(e)); }
}

export default function NominasTab({ canManage, canViewEvolucion }) {
  const [empId, setEmpId] = useState("all");
  const [upload, setUpload] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [detail, setDetail] = useState(null);
  const qc = useQueryClient();

  const teamQ = useQuery({ queryKey: ["rrhh-team-users"], queryFn: async () => (await api.get("/rrhh/team-users")).data, enabled: canManage });
  const team = teamQ.data?.items || [];

  const listParams = canManage && empId !== "all" ? { user_id: empId } : {};
  const listQ = useQuery({
    queryKey: ["rrhh-nominas", canManage ? empId : "self"],
    queryFn: async () => (await api.get("/rrhh/nominas", { params: listParams })).data,
    refetchInterval: (query) => (query.state.data?.rows?.some((r) => r.extraction_status === "procesando") ? 4000 : false),
  });
  const rows = listQ.data?.rows || [];

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/rrhh/nominas/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rrhh-nominas"] }); setDel(null); toast.success("Nómina eliminada"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const reMut = useMutation({
    mutationFn: async (id) => (await api.post(`/rrhh/nominas/${id}/reprocess`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rrhh-nominas"] }); toast.info("Reprocesando con IA…"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="space-y-5" data-testid="rrhh-nominas">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><Wallet className="h-6 w-6 text-primary" />Nóminas</h1>
          <p className="text-sm text-muted-foreground">{canManage ? "Sube las nóminas en PDF o un ZIP; la IA extrae los importes y asigna cada nómina a su empleado." : "Tus nóminas por mes y año. Solo tú y RRHH podéis verlas."}</p>
        </div>
        {canManage && <Button onClick={() => setUpload(true)} data-testid="nom-upload-btn"><Upload className="mr-1.5 h-4 w-4" />Subir nómina</Button>}
      </div>

      {canManage ? (
        <Tabs defaultValue="listado">
          <div className="flex flex-wrap items-center gap-3">
            <TabsList data-testid="nom-subtabs">
              <TabsTrigger value="listado" data-testid="nom-tab-listado"><FileText className="mr-1.5 h-4 w-4" />Listado</TabsTrigger>
              {canViewEvolucion && <TabsTrigger value="evolucion" data-testid="nom-tab-evolucion"><TrendingUp className="mr-1.5 h-4 w-4" />Evolución</TabsTrigger>}
            </TabsList>
            <div className="min-w-[220px]">
              <Select value={empId} onValueChange={setEmpId}>
                <SelectTrigger className="h-9" data-testid="nom-emp-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los empleados</SelectItem>
                  {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value="listado" className="mt-4">
            <NominaTable rows={rows} loading={listQ.isLoading} canManage showEmp={empId === "all"} onDetail={setDetail} onReprocess={(r) => reMut.mutate(r.id)} onEdit={setEdit} onDel={setDel} />
          </TabsContent>
          {canViewEvolucion && (
            <TabsContent value="evolucion" className="mt-4">
              {empId === "all"
                ? <EmptyCard icon={Users} text="Selecciona un empleado para ver su evolución salarial." testid="nom-evol-pick" />
                : <Evolucion userId={empId} />}
            </TabsContent>
          )}
        </Tabs>
      ) : (
        <EmployeeNominas rows={rows} loading={listQ.isLoading} />
      )}

      {upload && <UploadDialog team={team} onClose={() => setUpload(false)} />}
      {edit && <EditDialog payslip={edit} team={team} onClose={() => setEdit(null)} />}
      {detail && <DetailDialog payslip={detail} onClose={() => setDetail(null)} />}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta nómina?</AlertDialogTitle>
            <AlertDialogDescription>Se borrará el PDF y los importes de {del?.full_name || "el empleado"} ({del?.periodo_label || del?.periodo}).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => delMut.mutate(del.id)} data-testid="nom-del-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyCard({ icon: Icon, text, testid }) {
  return <Card><CardContent className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid={testid}><Icon className="h-6 w-6" />{text}</CardContent></Card>;
}

function StatusBadge({ s }) {
  if (s === "procesando") return <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600"><Loader2 className="mr-1 h-3 w-3 animate-spin" />Procesando</Badge>;
  if (s === "ok") return <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700"><Sparkles className="mr-1 h-3 w-3" />IA</Badge>;
  if (s === "manual") return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Manual</Badge>;
  return <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700"><AlertTriangle className="mr-1 h-3 w-3" />Revisar</Badge>;
}

function PayslipTable({ rows, showEmp, onDetail, onReprocess, onEdit, onDel }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            {showEmp && <th className="p-3">Empleado</th>}
            <th className="p-3">Periodo</th><th className="p-3 text-right">Bruto</th><th className="p-3 text-right">Neto</th>
            <th className="p-3 text-right">IRPF</th><th className="p-3 text-right">Coste empresa</th><th className="p-3">Estado</th><th className="p-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y" data-testid="nom-table">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`nom-row-${r.id}`}>
              {showEmp && <td className="p-3 font-medium">{r.full_name || <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700" data-testid={`nom-unassigned-${r.id}`}>Sin asignar</Badge>}</td>}
              <td className="p-3">{r.periodo_label || r.periodo || <span className="text-muted-foreground">s/f</span>}</td>
              <td className="p-3 text-right tabular-nums">{eur(r.bruto)}</td>
              <td className="p-3 text-right tabular-nums">{eur(r.neto)}</td>
              <td className="p-3 text-right tabular-nums">{eur(r.irpf)}</td>
              <td className="p-3 text-right tabular-nums">{eur(r.coste_empresa)}</td>
              <td className="p-3"><StatusBadge s={r.extraction_status} /></td>
              <td className="p-3">
                <div className="flex items-center justify-end gap-1">
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => onDetail(r)} data-testid={`nom-detail-${r.id}`}><Receipt className="mr-1 h-3.5 w-3.5" />Desglose</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => openPdf(r.id)} data-testid={`nom-pdf-${r.id}`}><FileText className="mr-1 h-3.5 w-3.5" />PDF</Button>
                  {onReprocess && r.extraction_status !== "procesando" && <Button size="icon" variant="ghost" className="h-8 w-8 text-indigo-600" onClick={() => onReprocess(r)} data-testid={`nom-reprocess-${r.id}`} title="Reprocesar con IA"><RefreshCw className="h-3.5 w-3.5" /></Button>}
                  {onEdit && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(r)} data-testid={`nom-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                  {onDel && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => onDel(r)} data-testid={`nom-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Pill = ({ children, className = "" }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${className}`}>{children}</span>
);

function NominaTable({ rows, loading, canManage, showEmp, onDetail, onReprocess, onEdit, onDel }) {
  if (loading) return <Skeleton className="h-48 w-full" />;
  if (rows.length === 0) return <EmptyCard icon={FileText} text="No hay nóminas registradas." testid="nom-empty" />;

  const byYear = {};
  rows.forEach((r) => {
    const per = r.periodo || "";
    const y = /^\d{4}/.test(per) ? per.slice(0, 4) : "Sin fecha";
    const m = /^\d{4}-\d{2}/.test(per) ? per.slice(5, 7) : "00";
    byYear[y] = byYear[y] || {};
    (byYear[y][m] = byYear[y][m] || []).push(r);
  });
  const years = Object.keys(byYear).sort((a, b) => (a === "Sin fecha" ? 1 : b === "Sin fecha" ? -1 : b.localeCompare(a)));
  const latestYear = years[0];

  return (
    <div className="space-y-2" data-testid="nom-folders">
      <Accordion type="multiple" defaultValue={[latestYear]} className="space-y-2">
        {years.map((y) => {
          const months = Object.keys(byYear[y]).sort((a, b) => b.localeCompare(a));
          const yearRows = months.flatMap((m) => byYear[y][m]);
          const latestMonth = `${y}-${months[0]}`;
          return (
            <AccordionItem key={y} value={y} className="overflow-hidden rounded-xl border bg-card">
              <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid={`nom-year-${y}`}>
                <span className="flex flex-1 items-center gap-2.5 pr-3">
                  <FolderOpen className="h-5 w-5 text-primary" />
                  <span className="font-heading text-lg font-bold tracking-tight">{y}</span>
                  <Pill className="border-primary/20 bg-primary/5 text-primary">{yearRows.length} nóminas</Pill>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-2 sm:px-3">
                <Accordion type="multiple" defaultValue={y === latestYear ? [latestMonth] : []} className="space-y-1.5">
                  {months.map((m) => {
                    const mrows = byYear[y][m];
                    const label = m === "00" ? "Sin periodo" : MESES_NOM[parseInt(m, 10)];
                    return (
                      <AccordionItem key={m} value={`${y}-${m}`} className="overflow-hidden rounded-lg border bg-background">
                        <AccordionTrigger className="px-3 py-2.5 hover:no-underline" data-testid={`nom-month-${y}-${m}`}>
                          <span className="flex flex-1 items-center gap-2 pr-3">
                            <Folder className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{label}</span>
                            <Pill className="bg-muted text-muted-foreground">{mrows.length}</Pill>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="p-0">
                          <PayslipTable rows={mrows} showEmp={showEmp} onDetail={onDetail} onReprocess={onReprocess} onEdit={onEdit} onDel={onDel} />
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

const MESES_ABR = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const YEAR_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ec4899", "#0ea5e9", "#8b5cf6"];
const METRICS = [
  { key: "neto", label: "Neto" },
  { key: "bruto", label: "Bruto" },
  { key: "comisiones", label: "Comisiones" },
];
const r2 = (n) => Math.round((n || 0) * 100) / 100;

function DiffBadge({ value, pct }) {
  if (value == null) return <span className="text-xs text-muted-foreground">—</span>;
  const up = value > 0, flat = Math.abs(value) < 0.005;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const cls = flat ? "text-muted-foreground" : up ? "text-emerald-600" : "text-rose-600";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`}>
      <Icon className="h-3.5 w-3.5" />{eur(Math.abs(value))}{pct != null ? ` (${up ? "+" : ""}${pct}%)` : ""}
    </span>
  );
}

function Evolucion({ userId }) {
  const [metric, setMetric] = useState("neto");
  const evolQ = useQuery({
    queryKey: ["rrhh-nomina-evol", userId || "self"],
    queryFn: async () => (await api.get("/rrhh/nominas/evolucion", { params: userId ? { user_id: userId } : {} })).data,
  });
  const points = evolQ.data?.points || [];
  const anios = evolQ.data?.por_anio || [];
  const years = anios.map((a) => a.anio);

  // 1) Comparativa mensual año a año (pivot mes 1-12, una serie por año)
  const monthly = useMemo(() => {
    const byMonth = {};
    for (let m = 1; m <= 12; m++) byMonth[m] = { mes: MESES_ABR[m] };
    points.forEach((p) => {
      if (!p.mes) return;
      byMonth[p.mes][p.anio] = r2((byMonth[p.mes][p.anio] || 0) + (p[metric] || 0));
    });
    return Object.values(byMonth);
  }, [points, metric]);

  // 2) Cobro medio mensual por año
  const medios = anios.map((a) => ({ name: a.anio, "Bruto medio/mes": a.bruto_medio, "Neto medio/mes": a.neto_medio }));

  // 3) Diferencial de comisiones año a año
  const comis = anios.map((a, i) => {
    const prev = i > 0 ? anios[i - 1] : null;
    const diff = prev ? r2(a.comisiones - prev.comisiones) : null;
    const pct = prev && prev.comisiones ? r2(((a.comisiones - prev.comisiones) / prev.comisiones) * 100) : null;
    return { name: a.anio, Comisiones: a.comisiones, "Comisiones medio/mes": a.comisiones_medio, diff, pct };
  });

  if (evolQ.isLoading) return <Skeleton className="h-64 w-full" />;
  if (points.length === 0) return <EmptyCard icon={TrendingUp} text="Aún no hay nóminas para mostrar la evolución." testid="nom-evol-empty" />;

  const metricLabel = METRICS.find((m) => m.key === metric)?.label || "";

  return (
    <div className="space-y-5">
      {/* 1) Comparativa mensual año a año */}
      <Card><CardContent className="p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Comparativa mensual año a año · {metricLabel}</h3>
          <div className="flex items-center gap-1 rounded-lg border bg-muted/40 p-0.5" data-testid="nom-evol-metric">
            {METRICS.map((m) => (
              <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`nom-metric-${m.key}`}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${metric === m.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <p className="mb-2 text-xs text-muted-foreground">Cada línea es un año; el eje son los meses (Ene–Dic). Así comparas el mismo mes entre años.</p>
        <div className="h-72 w-full" data-testid="nom-evol-yoy">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthly} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              {years.map((y, i) => (
                <Line key={y} type="monotone" dataKey={y} name={y} stroke={YEAR_COLORS[i % YEAR_COLORS.length]} strokeWidth={2.5} dot={{ r: 2 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent></Card>

      {/* 2) Cobro medio mensual por año */}
      <Card><CardContent className="p-4">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cobro medio mensual por año</h3>
        <p className="mb-3 text-xs text-muted-foreground">Media de lo cobrado al mes en cada año (total del año ÷ nº de nóminas).</p>
        <div className="h-60 w-full" data-testid="nom-evol-medios">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={medios} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              <Bar dataKey="Bruto medio/mes" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Neto medio/mes" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <table className="mt-3 w-full text-sm" data-testid="nom-evol-medios-table">
          <thead className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="py-2">Año</th><th className="py-2 text-right">Bruto medio/mes</th><th className="py-2 text-right">Neto medio/mes</th><th className="py-2 text-right">Nóminas</th></tr>
          </thead>
          <tbody className="divide-y">
            {anios.map((a) => (
              <tr key={a.anio}>
                <td className="py-1.5 font-medium">{a.anio}</td>
                <td className="py-1.5 text-right tabular-nums">{eur(a.bruto_medio)}</td>
                <td className="py-1.5 text-right tabular-nums text-emerald-600">{eur(a.neto_medio)}</td>
                <td className="py-1.5 text-right tabular-nums">{a.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      {/* 3) Diferencial de comisiones año a año */}
      <Card><CardContent className="p-4">
        <h3 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Percent className="h-3.5 w-3.5" />Diferencial de comisiones año a año</h3>
        <p className="mb-3 text-xs text-muted-foreground">Comisiones totales por año y su variación respecto al año anterior.</p>
        <div className="h-56 w-full" data-testid="nom-evol-comisiones">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={comis} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              <Bar dataKey="Comisiones" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <table className="mt-3 w-full text-sm" data-testid="nom-evol-comisiones-table">
          <thead className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="py-2">Año</th><th className="py-2 text-right">Comisiones</th><th className="py-2 text-right">Media/mes</th><th className="py-2 text-right">Dif. vs año anterior</th></tr>
          </thead>
          <tbody className="divide-y">
            {comis.map((c) => (
              <tr key={c.name}>
                <td className="py-1.5 font-medium">{c.name}</td>
                <td className="py-1.5 text-right tabular-nums">{eur(c.Comisiones)}</td>
                <td className="py-1.5 text-right tabular-nums">{eur(c["Comisiones medio/mes"])}</td>
                <td className="py-1.5 text-right"><DiffBadge value={c.diff} pct={c.pct} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

function UploadDialog({ team, onClose }) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState("");
  const [periodo, setPeriodo] = useState("");
  const [files, setFiles] = useState([]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["rrhh-nominas"] });
    qc.invalidateQueries({ queryKey: ["rrhh-nominas-costes"] });
    qc.invalidateQueries({ queryKey: ["rrhh-nomina-evol"] });
  };
  const hasZip = files.some((f) => f.name.toLowerCase().endsWith(".zip"));
  const single = files.length === 1 && !!userId && !hasZip;

  const upMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      const cfg = { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 };
      if (single) {
        fd.append("user_id", userId);
        if (periodo) fd.append("periodo", periodo);
        fd.append("file", files[0]);
        return { mode: "single", data: (await api.post("/rrhh/nominas", fd, cfg)).data };
      }
      files.forEach((f) => fd.append("files", f));
      return { mode: "bulk", data: (await api.post("/rrhh/nominas/bulk", fd, cfg)).data };
    },
    onSuccess: ({ mode, data }) => {
      invalidate();
      if (mode === "single") {
        if (data.extraction_status === "error") toast.warning("PDF subido, pero la IA no pudo leer los importes. Edítalos a mano.");
        else toast.success("Nómina subida y procesada");
      } else {
        toast.success(`${data.created} nómina(s) subidas. Procesando y asignando con IA…`);
      }
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="nom-upload-dialog">
        <DialogHeader>
          <DialogTitle>Subir nóminas</DialogTitle>
          <DialogDescription>Sube <strong>varios PDF</strong> o un <strong>ZIP</strong> con las nóminas (ideal para años anteriores). La IA (Gemini 3.1 Pro) lee los importes y <strong>asigna cada nómina a su empleado</strong> por DNI o nombre. Las que no pueda asignar quedarán como «Sin asignar» para corregirlas.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Archivos (PDF o ZIP) *</Label>
            <Input type="file" accept="application/pdf,.zip,application/zip,application/x-zip-compressed" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="nom-up-file" />
            {files.length > 0 && <p className="text-xs text-muted-foreground" data-testid="nom-up-count">{files.length} archivo(s) seleccionado(s){hasZip ? " · incluye ZIP" : ""}.</p>}
          </div>
          <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
            <p className="text-xs font-medium text-muted-foreground">Opcional — solo al subir 1 PDF, si quieres forzar el empleado:</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Forzar empleado</Label>
                <Select value={userId} onValueChange={setUserId} disabled={files.length > 1 || hasZip}>
                  <SelectTrigger data-testid="nom-up-emp"><SelectValue placeholder="Auto (por IA)" /></SelectTrigger>
                  <SelectContent>{team.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Periodo</Label>
                <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} disabled={files.length > 1 || hasZip} data-testid="nom-up-periodo" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => upMut.mutate()} disabled={files.length === 0 || upMut.isPending} data-testid="nom-up-submit">
            {upMut.isPending ? "Subiendo…" : single ? "Subir y procesar" : `Subir${files.length ? ` ${files.length}` : ""} y autodistribuir`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EDIT_FIELDS = [
  ["bruto", "Bruto (€)"], ["neto", "Neto (€)"], ["irpf", "IRPF (€)"],
  ["ss_trabajador", "SS trabajador (€)"], ["coste_empresa", "Coste empresa (€)"],
];

function EditDialog({ payslip, team = [], onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    user_id: payslip.user_id || "",
    periodo: payslip.periodo || "",
    bruto: payslip.bruto ?? "", neto: payslip.neto ?? "", irpf: payslip.irpf ?? "",
    ss_trabajador: payslip.ss_trabajador ?? "", coste_empresa: payslip.coste_empresa ?? "",
  }));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = { user_id: form.user_id || "", periodo: form.periodo };
      EDIT_FIELDS.forEach(([k]) => { payload[k] = form[k] === "" ? null : Number(form[k]); });
      return (await api.put(`/rrhh/nominas/${payslip.id}`, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rrhh-nominas"] });
      qc.invalidateQueries({ queryKey: ["rrhh-nominas-costes"] });
      qc.invalidateQueries({ queryKey: ["rrhh-nomina-evol"] });
      toast.success("Nómina actualizada"); onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="nom-edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar nómina · {payslip.full_name || "Sin asignar"}</DialogTitle>
          <DialogDescription>Asigna el empleado correcto y corrige los importes que la IA pudo extraer mal.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Empleado</Label>
            <Select value={form.user_id} onValueChange={(v) => set("user_id", v)}>
              <SelectTrigger data-testid="nom-edit-emp"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
              <SelectContent>{team.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Periodo (YYYY-MM)</Label>
            <Input type="month" value={form.periodo} onChange={(e) => set("periodo", e.target.value)} data-testid="nom-edit-periodo" />
          </div>
          {EDIT_FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label>{label}</Label>
              <Input type="number" step="0.01" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`nom-edit-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="nom-edit-save">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MiniStat({ label, v, strong, accent }) {
  return (
    <div className="rounded-lg border p-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-sm font-semibold tabular-nums ${accent ? "text-amber-600" : strong ? "text-emerald-600" : ""}`}>{eur(v)}</p>
    </div>
  );
}

function ItemsTable({ title, items, testid }) {
  const total = items.reduce((s, i) => s + (i.importe || 0), 0);
  return (
    <div className="space-y-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">—</p>
      ) : (
        <table className="w-full text-sm" data-testid={testid}>
          <tbody className="divide-y">
            {items.map((it, i) => (
              <tr key={i}>
                <td className="py-1.5 pr-2">{it.concepto}</td>
                <td className="py-1.5 text-right tabular-nums">{eur(it.importe)}</td>
              </tr>
            ))}
            <tr className="border-t-2 font-semibold">
              <td className="py-1.5">Total</td>
              <td className="py-1.5 text-right tabular-nums">{eur(total)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function DetailDialog({ payslip, onClose }) {
  const dev = payslip.devengos || [];
  const ded = payslip.deducciones || [];
  const emp = payslip.aportacion_empresa || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="nom-detail-dialog">
        <DialogHeader>
          <DialogTitle>Desglose · {payslip.full_name || "Sin asignar"}</DialogTitle>
          <DialogDescription>{payslip.periodo_label || payslip.periodo || "Sin periodo"}{payslip.categoria ? ` · ${payslip.categoria}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <MiniStat label="Bruto" v={payslip.bruto} />
          <MiniStat label="Neto" v={payslip.neto} strong />
          <MiniStat label="IRPF" v={payslip.irpf} />
          <MiniStat label="SS trab." v={payslip.ss_trabajador} />
          <MiniStat label="Coste empresa" v={payslip.coste_empresa} accent />
        </div>
        <ItemsTable title="Devengos" items={dev} testid="nom-detail-devengos" />
        <ItemsTable title="Deducciones" items={ded} testid="nom-detail-deducciones" />
        {emp.length > 0 && <ItemsTable title="Aportación de la empresa (Seguridad Social)" items={emp} testid="nom-detail-empresa" />}
        <DialogFooter>
          <Button variant="outline" onClick={() => openPdf(payslip.id)} data-testid="nom-detail-pdf"><FileText className="mr-1.5 h-4 w-4" />Ver PDF original</Button>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EmployeeNominas({ rows, loading }) {
  if (loading) return <Skeleton className="h-48 w-full" />;
  if (rows.length === 0) return <EmptyCard icon={FileText} text="Todavía no tienes nóminas disponibles." testid="nom-empty" />;
  const byYear = {};
  rows.forEach((r) => {
    const y = (r.periodo || "").slice(0, 4) || "Sin fecha";
    (byYear[y] = byYear[y] || []).push(r);
  });
  const years = Object.keys(byYear).sort().reverse();
  return (
    <div className="space-y-6" data-testid="emp-nominas-list">
      {years.map((y) => (
        <div key={y}>
          <h2 className="mb-2 font-heading text-lg font-semibold">{y}</h2>
          <Card><CardContent className="divide-y p-0">
            {byYear[y].map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-3" data-testid={`emp-nomina-${r.id}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-4 w-4" /></span>
                  <span className="font-medium">{r.periodo_label || r.periodo || r.filename}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" className="h-8" onClick={() => openPdf(r.id)} data-testid={`emp-nomina-view-${r.id}`}><FileText className="mr-1 h-3.5 w-3.5" />Ver</Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => downloadPdf(r.id, r.filename)} data-testid={`emp-nomina-dl-${r.id}`}><Download className="mr-1 h-3.5 w-3.5" />Descargar</Button>
                </div>
              </div>
            ))}
          </CardContent></Card>
        </div>
      ))}
    </div>
  );
}
